import { formatPesoAmount } from '../../utils/money';

const parseNonNegativeNumber = (value) => {
  if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed === 0 ? 0 : parsed;
};

export const getQuickRuleReferenceCost = (format, basis = 'unit') => {
  if (!['unit', 'kg'].includes(basis) || format?.analysis?.pricingBasis !== basis) return null;
  // Rules use the unrounded cost in their own basis, not the rounded sale-unit cost.
  return parseNonNegativeNumber(format.analysis.referenceCost?.basisValue);
};

export const getQuickRuleEquivalent = (rule, referenceCost) => {
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)
    || !['markup_percent', 'markup_amount', 'fixed_price'].includes(rule.mode)) return null;
  const ruleValue = parseNonNegativeNumber(rule.value);
  if (ruleValue === null || (rule.mode !== 'markup_percent' && !Number.isInteger(ruleValue))) return null;
  const cost = parseNonNegativeNumber(referenceCost);
  if (cost === null) return { unavailable: 'missing_cost' };
  if (rule.mode !== 'markup_percent' && cost === 0) return { unavailable: 'zero_cost' };

  const value = rule.mode === 'markup_percent'
    ? cost * ruleValue / 100
    : (rule.mode === 'fixed_price' ? ruleValue - cost : ruleValue) / cost * 100;
  if (!Number.isFinite(value)) return { unavailable: 'invalid_result' };
  return { mode: rule.mode === 'markup_percent' ? 'amount' : 'percent', value };
};

export const getQuickRuleMargin = (rule, referenceCost) => {
  const equivalent = getQuickRuleEquivalent(rule, referenceCost);
  if (!equivalent || equivalent.unavailable) return null;
  const cost = Number(referenceCost);
  const amount = rule.mode === 'fixed_price' ? Number(rule.value) - cost
    : rule.mode === 'markup_amount' ? Number(rule.value) : cost * Number(rule.value) / 100;
  const percent = cost > 0 ? amount / cost * 100 : null;
  return { amount, percent };
};

export const formatSignedRuleValue = (value, mode = 'percent', maximumFractionDigits = 3) => (
  `${value < 0 ? '- ' : ''}${mode === 'amount' ? '$' : ''}${mode === 'amount' ? formatPesoAmount(Math.abs(value)) : Math.abs(value).toLocaleString('es-CL', { maximumFractionDigits })}${mode === 'amount' ? '' : '%'}`
);

export const getQuickRuleBadgeMargin = (rule, referenceCost) => {
  const value = parseNonNegativeNumber(rule?.value);
  if (value === null) return null;
  // An explicit percentage is still known when the cost is missing or zero.
  if (rule.mode === 'markup_percent') {
    const equivalent = getQuickRuleEquivalent(rule, referenceCost);
    return { percent: value, amount: equivalent?.value ?? null };
  }
  return getQuickRuleMargin(rule, referenceCost);
};

export const formatRuleMarginAmount = (amount) => (
  `${amount < 0 ? '-' : ''}$${formatPesoAmount(Math.abs(amount))}`
);

export const formatMarginRuleLabel = (rule, referenceCost, basis = rule?.basis || 'unit') => {
  if (!rule) return 'Sin regla';
  const margin = getQuickRuleBadgeMargin(rule, referenceCost);
  if (!margin) return 'Pendiente';
  const percent = formatSignedRuleValue(margin.percent, 'percent', rule.mode === 'markup_percent' ? 3 : 2);
  const showAmount = rule.mode === 'markup_amount' && margin.amount !== null;
  return showAmount
    ? `${formatRuleMarginAmount(margin.amount)}${basis === 'kg' ? ' / kg' : ''} ≈ ${percent}`
    : percent;
};
